"use client";

import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAccessToken, useTenantData } from "@/hooks/shared";
import { invalidateUserTaskQueries } from "@/lib/user-tasks/invalidate-user-task-queries";
import { createAuthenticatedApi } from "@/lib/utils";
import {
  UserTasksApi,
  type UpdateUserTaskRequest,
  type UserTaskStatus,
} from "@/openapi/generated";

export type UpdateUserTaskInput = {
  taskId: string;
  assigneeUserId: number | null;
  status: UserTaskStatus | null;
};

export function useUserTaskUpdate() {
  const accessToken = useAccessToken();
  const { tenantId } = useTenantData();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (input: UpdateUserTaskInput) => {
      if (!accessToken || tenantId == null) {
        throw new Error("Authentication required");
      }
      const service = createAuthenticatedApi(UserTasksApi, accessToken);
      const body: UpdateUserTaskRequest = {
        assigneeUserId: input.assigneeUserId,
        status: input.status,
      };
      await service.apiUserTasksIdPut({
        id: input.taskId,
        updateUserTaskRequest: body,
        xTenantID: tenantId,
      });
    },
    onSuccess: async () => {
      await invalidateUserTaskQueries(queryClient, tenantId);
    },
  });

  return {
    updateUserTask: mutation.mutate,
    updateUserTaskAsync: mutation.mutateAsync,
    isUpdating: mutation.isPending,
  };
}
